import React, {
  useCallback,
  useState,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { TypographyVariant } from 'apps/sigma2-ui/src/components/ui/Typography';
import { variantToClassNameWithoutTextColor } from 'apps/sigma2-ui/src/components/ui/Typography';
import classnames from 'classnames';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { getTranslatedContentAsString } from 'libs/end-to-end/src/translation-content';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { IconCheckMark24pxSvg } from 'apps/sigma2-ui/src/components/svg/IconCheckMark24px';
import { IconCloseSvg } from 'apps/sigma2-ui/src/components/svg/IconClose';
import { Spinner } from 'apps/sigma2-ui/src/components/ui/Spinner';
import classNames from 'classnames';
import { useEmbedMode } from 'apps/sigma2-ui/src/components/EmbedModeContext';

export const EditableName = ({
  name,
  size,
  isEditing: isEditingProp,
  onAfterSave,
  className: headerClassName,
}: {
  name: string;
  size: TypographyVariant;
  isEditing?: boolean;
  onAfterSave: (name: string) => void;
  className?: string;
}) => {
  const language = useLanguage();
  const [editingName, setEditingName] = useState<string>(name);
  const isEmbedMode = useEmbedMode();
  const editingNameRef = useRef<HTMLInputElement>(null);

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const isEditingComputed = useMemo(
    () => isEditing && !isEmbedMode,
    [isEditing, isEmbedMode],
  );
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  useEffect(() => {
    setIsEditing(isEditingProp ?? false);
  }, [isEditingProp]);

  const spanRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (editingNameRef.current && isEditingComputed) {
      editingNameRef.current.focus();
    }
  }, [editingName, isEditingComputed]);

  useEffect(() => {
    setEditingName(name);
  }, [isEditingComputed, name]);

  const handleOnDashboardNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setEditingName(e.target.value);
    },
    [],
  );

  const cancelEdit = useCallback(async () => {
    await onAfterSave(name);
    setIsEditing(false);
  }, [name, onAfterSave]);

  const saveName = useCallback(async () => {
    setIsUpdating(true);
    await onAfterSave(editingName);
    setIsEditing(false);
    setIsUpdating(false);
  }, [onAfterSave, editingName]);

  const handleKeyDown = useCallback(
    async (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape') {
        await cancelEdit();
        e.preventDefault();
      } else if (e.key === 'Enter') {
        await saveName();
      }
    },
    [cancelEdit, saveName],
  );

  const shouldEditOnClick = useMemo(
    () => isEditingProp === undefined && !isEmbedMode,
    [isEditingProp, isEmbedMode],
  );

  if (isEditingComputed) {
    return (
      <div className="relative -ml-2.5 w-full">
        <input
          ref={editingNameRef}
          type="text"
          value={editingName}
          onChange={handleOnDashboardNameChange}
          onKeyDown={handleKeyDown}
          placeholder="Enter text..."
          className={classnames(
            'form-input bg-transparent -my-1 px-2 py-1 grow-0 text-gray-900 !ring-0 border-2 border-solid rounded border-blue-600 w-full',
            [variantToClassNameWithoutTextColor[size]],
          )}
        />
        <div className="absolute right-0 top-full">
          <div className="mt-2 flex gap-1">
            <Button
              variant="outline"
              innerClassName={[
                'border-0 !px-2 bg-white drop-shadow-lg border-1 border-gray-100 border-solid !ring-0 !ring-offset-0',
              ]}
              size="small"
              role="button"
              behavior={{
                type: 'onClick',
                onClick: saveName,
              }}
            >
              {isUpdating ? (
                <Spinner size={16} textColor="text-gray-900" />
              ) : (
                <IconCheckMark24pxSvg svg={{ width: 16, height: 16 }} />
              )}
            </Button>
            <Button
              variant="outline"
              innerClassName={[
                'border-0 !px-2 bg-white drop-shadow-lg border-1 border-gray-100 border-solid !ring-0 !ring-offset-0',
              ]}
              size="small"
              role="button"
              behavior={{
                type: 'onClick',
                onClick: cancelEdit,
              }}
            >
              <IconCloseSvg svg={{ width: 16, height: 16 }} />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={classNames('-ml-2.5 rounded', {
        'hover:cursor-text': shouldEditOnClick,
      })}
      ref={spanRef}
      onClick={() => {
        shouldEditOnClick && setIsEditing(true);
      }}
    >
      <div
        className={classnames(
          'transition-colors duration-300 rounded w-full px-2 -my-1 mx-0.5 py-1.5',
          { 'hover:bg-gray-100': shouldEditOnClick },
        )}
      >
        <S2Typography
          variant={size}
          textColorVariant="gray-900"
          translatedContent={language.dangerousUserInput({
            value: name,
            fallback: getTranslatedContentAsString(language.addADescription()),
          })}
          dangerouslyOverrideClassNameFn={(className) =>
            classnames(className, headerClassName, 'line-clamp-1')
          }
        />
      </div>
    </div>
  );
};
